/* eslint-disable no-nested-ternary */
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Card from '@/components/Card/Card';
import Header from '@/components/Header/Header';
import Icon from '@/components/Icons/Icon';
import Link from '@/components/Link/Link';
import ConfirmationModal from '@/components/Modal/ConfirmationModal/ConfirmationModal';
import Table from '@/components/Tables/Table/Table';
import sharedTableStyles from '@/components/Tables/SharedTableStyles.module.css';
import Tag from '@/components/Tag/Tag';
import { TagTypes } from '@/components/Tag/Tag.types';
import TitleWithInfoIcon from '@/components/Title/TitleWithInfoIcon';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import Tooltip from '@/components/Tooltip/Tooltip';
import { PositionTypes } from '@/components/Tooltip/Tooltip.types';
import BundleDetailsPlaceholder from '@/images/BundleDetailsPlaceholder.svg';
import {
  useBundleDetails,
  useDeactivateBundle,
  useEditBundlePrice,
  useEditLinkedBundlePrice,
  useFreezeBundle,
  useMaintenanceFreezeDetails,
  useNationalProgramCurrent,
  useStoreGroups,
} from '@/services/bundles/bundles.service';
import { buildBundleProductChoicesTableData } from '@/services/bundles/bundles.service.helper';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useStoreBundleGroups } from '@/services/groups/groups.service';
import { UserRoles } from '@/types/auth.types';
import { StoreGroup } from '@/types/bundles.types';
import { NationalProgramStates } from '@/types/nationalProgram.types';
import { SubmissionStatus, SubmissionStatusLabels } from '@/types/submission.types';
import { renderLockedSettings, renderOccasions } from '@/utils/formatting/formatBundleProperties';
import { useBundleAddOnsFeatureFlag } from '@/services/featureFlags/featureFlags.service';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import useLocalUser from '@/utils/useLocalUser/useLocalUser';
import useNavigateWithToast from '@/utils/useNavigateWithToast/useNavigateWithToast';
import LinkIcon from '@mui/icons-material/Link';
import { Avatar, Chip } from '@mui/material';
import { CellContext, Row } from '@tanstack/react-table';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styles from './BundleDetails.module.css';
import DeactivateBundleModal from './DeactivateBundleModal/DeactivateBundleModal';
import MissingProductsModal from './MissingProductsModal/MissingProductsModal';
import EditBundleSettingsModal from './EditBundleSettingsModal/EditBundleSettingsModal';
import { PriceOptions } from './EditBundleSettingsModal/EditBundleSettingsModal.types';
import { LinkedBundle } from './Internal/LinkedBundle';
import LinkedBundleModal from './LinkedBundleModal/LinkedBundleModal';
import NationalProgram from './NationalProgram/NationalProgram';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

const ChosenPriceCell = ({ row }: CellContext<StoreGroup, unknown>) => {
  const { t } = useTranslation();
  const { previousPrice, chosenPrice, status } = row.original;
  if (status === SubmissionStatus.PENDING) {
    if (previousPrice === null && chosenPrice === null) return t('common.inactive');
    if (previousPrice === chosenPrice) return `$${chosenPrice.toFixed(2)}`;
    return (
      <div className={styles.chosenPriceCell}>
        <span className={styles.strikethrough}>
          {previousPrice === 99.99 ? t('common.inactive') : `$${previousPrice?.toFixed(2)}`}
        </span>
        <Icon iconName="ChevronRight" />
        <span>{chosenPrice || chosenPrice === 0 ? `$${chosenPrice.toFixed(2)}` : t('common.inactive')}</span>
      </div>
    );
  }
  return (chosenPrice && chosenPrice !== 99.99) || chosenPrice === 0
    ? `$${chosenPrice.toFixed(2)}`
    : t('common.inactive');
};
const ChosenRedeemOnlineCell = ({ row }: CellContext<StoreGroup, unknown>) => {
  const { t } = useTranslation();
  const { previousPrice, chosenPrice, previousRedeemableOnline, chosenRedeemableOnline, status } = row.original;

  if (status === SubmissionStatus.PENDING) {
    if (!previousPrice && (!chosenPrice || chosenPrice.toFixed(2) === '99.99')) return t('common.inactive');
    if (previousPrice != null && chosenPrice != null && previousRedeemableOnline === chosenRedeemableOnline)
      return chosenRedeemableOnline ? t('common.yes') : t('common.no');
    return (
      <div className={styles.chosenPriceCell}>
        <span className={styles.strikethrough}>
          {previousPrice === null ? t('common.inactive') : previousRedeemableOnline ? t('common.yes') : t('common.no')}
        </span>
        <Icon iconName="ChevronRight" />
        <span>{chosenPrice === null ? 'Inactive' : chosenRedeemableOnline ? t('common.yes') : t('common.no')}</span>
      </div>
    );
  }
  return chosenPrice === null ? '' : chosenRedeemableOnline ? t('common.yes') : t('common.no');
};

const ChosenVisibleOnlineCell = ({ row }: CellContext<StoreGroup, unknown>) => {
  const { t } = useTranslation();
  const { previousPrice, chosenPrice, previousVisibleOnline, chosenVisibleOnline, status } = row.original;
  if (status === SubmissionStatus.PENDING) {
    if (previousPrice === null && chosenPrice === null) return t('common.inactive');
    if (previousPrice != null && chosenPrice != null && previousVisibleOnline === chosenVisibleOnline)
      return chosenVisibleOnline ? t('common.yes') : t('common.no');
    return (
      <div className={styles.chosenPriceCell}>
        <span className={styles.strikethrough}>
          {previousPrice === null ? t('common.inactive') : previousVisibleOnline ? t('common.yes') : t('common.no')}
        </span>
        <Icon iconName="ChevronRight" />
        <span>
          {chosenPrice === null ? t('common.inactive') : chosenVisibleOnline ? t('common.yes') : t('common.no')}
        </span>
      </div>
    );
  }
  return chosenPrice === null ? '' : chosenVisibleOnline ? t('common.yes') : t('common.no');
};

const Status = ({ row }: CellContext<StoreGroup, unknown>) => {
  const { t } = useTranslation();
  const labelType: 'Pending' | 'Completed' = SubmissionStatusLabels[row.original.status as SubmissionStatus];
  const label = t(`submissionStatusLabels.${row.original.status as SubmissionStatus}`) as string;
  if (row.original.status) {
    return <Tag label={label} type={TagTypes[labelType]} />;
  }
  return <p style={{ fontStyle: 'italic', fontSize: 12 }}>{t('submissionStatusLabels.noPendingUpdates')}</p>;
};

const StoreGroupCell = ({ row }: CellContext<StoreGroup, unknown>) => (
  <span
    className={classNames(styles.storeGroupName, {
      [styles.storeGroupNameWraps]: row.original.groupName.length > 20,
    })}
  >
    {row.original.groupName}
  </span>
);

const MissingItemsCell = ({ row }: CellContext<StoreGroup, unknown>) => {
  const { t } = useTranslation();

  if (!row.original.isVariantMissing) {
    return null;
  }

  return <span className={styles.missingItemsLabel}>{t('editBundle.bundleSettings.missingItemsLabel')}</span>;
};

const BundleDetails: FC = () => {
  const { t } = useTranslation();
  const { user } = useLocalUser();
  const isViewOnly = user?.role === UserRoles.VIEW;
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const { alias } = useFranchiseeAlias();
  const { bundleId } = useParams();
  const navigateWithToast = useNavigateWithToast();
  const [selectedPrice, setSelectedPrice] = useState<string | undefined>(undefined);
  const [hideInactive, setHideInactive] = useState(false);
  const [selectedRows, setSelectedRows] = useState<{ [number: number]: boolean }>({});
  const [isEditBundleSettingsModalOpen, setIsEditBundleSettingsModalOpen] = useState<boolean>(false);
  const [isLinkedBundleModalOpen, setIsLinkedBundleModalOpen] = useState<boolean>(false);
  const [showConfirmedModal, setShowConfirmedModal] = useState(false);
  const [dropdownPriceOptions, setDropdownPriceOptions] = useState<PriceOptions[] | undefined>();
  const [selectedLinkedBundleGroups, setSelectedLinkedBundleGroups] = useState<StoreGroup[] | undefined>(undefined);

  const { data: nationalProgramData, refetch: refetchNationalProgram } = useNationalProgramCurrent(
    bundleId !== undefined,
    bundleId!,
  );

  const { data: maintenanceFreezeData } = useMaintenanceFreezeDetails(bundleId !== undefined, bundleId!);

  const isMaintenanceFreeze = maintenanceFreezeData?.data?.bundleFrozen || false;

  const {
    data: bundleDetailsData,
    isLoading: bundleDetailsLoading,
    isError: bundleDetailsError,
    refetch: refetchBundleDetails,
  } = useBundleDetails(bundleId !== undefined, bundleId!);

  const activeStoreGroupCount = bundleDetailsData?.data?.fzGroupCount;

  const { data: storeBundleGroups } = useStoreBundleGroups(true);

  const {
    data: storeGroupsData,
    isLoading: storeGroupsLoading,
    isError: storeGroupsError,
    refetch,
  } = useStoreGroups(bundleId !== undefined, bundleId!);

  const storeGroupsDataWithoutStore = useMemo(() => {
    const bundleGroupWithStoreAccount =
      storeBundleGroups?.data.filter((group) => group.storesCount > 0).map((group) => group.id) || [];
    return storeGroupsData?.data
      ?.filter((group) => bundleGroupWithStoreAccount.includes(group.groupId))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));
  }, [storeBundleGroups?.data, storeGroupsData?.data]);

  const nationalProgramGroups: StoreGroup[] =
    nationalProgramData?.data.chosenPrices
      ?.sort((a, b) => a.groupName.localeCompare(b.groupName))
      .map((item) => ({
        groupId: item.groupId,
        chosenPrice: item.chosenPrice,
        previousPrice: null,
        chosenRedeemableOnline: false,
        chosenVisibleOnline: false,
        previousRedeemableOnline: false,
        previousVisibleOnline: false,
        groupName: item.groupName,
        createdAt: '',
        updatedAt: '',
        status: '',
        isExcluded: false,
      })) || [];

  const { mutate, isLoading: editBundlePriceLoading } = useEditBundlePrice(bundleId || '');

  const { mutate: linkedBundleMutate, isLoading: editLinkedBundleLoading } = useEditLinkedBundlePrice(bundleId || '');

  const {
    mutate: deactivateBundle,
    isLoading: deactivateIsLoading,
    reset: resetDeactivate,
  } = useDeactivateBundle(bundleId!);

  if (bundleDetailsError) {
    navigateWithToast('/', {
      label: t('error.fetchingBundleDetails'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingBundleDetails',
    });
  }

  if (storeGroupsError) {
    showToast({
      label: t('error.fetchingStoreGroups'),
      toastType: ToastTypes.Error,
      id: 'errorFetchingStoreGroups',
    });
  }

  const displayName = bundleDetailsData?.data?.name || '-';
  const bundleCode = bundleDetailsData?.data?.code || '-';
  const bundleDesc = bundleDetailsData?.data?.internalName || '-';
  const isInvisible = bundleDetailsData?.data?.isVisible === false;
  const isLinkedBundle = bundleDetailsData?.data?.linkedBundleName || null;
  const prices =
    bundleDetailsData?.data?.recommendedPrices
      ?.filter((price) => price || price === 0)
      .map((price) => `$${price.toFixed(2)}`)
      .join(', ') || '-';

  const isUpchargeRequired = bundleDetailsData?.data?.isUpchargeRequired;

  const codes = bundleDetailsData?.data?.bundleLinkedToDto;

  const occasion = renderOccasions(t, bundleDetailsData?.data?.occasions);
  const choiceDetails = bundleDetailsData?.data?.choiceDetails ?? [];
  const hasAnyAddOnChoice = choiceDetails.some((c) => c.addOnFlag);
  const productChoicesData = buildBundleProductChoicesTableData(choiceDetails);
  const isLockRedeemableOnline = bundleDetailsData?.data?.isLockRedeemableOnline;
  const isLockVisibleOnline = bundleDetailsData?.data?.isLockVisibleOnline;
  const lockRedeemableOnline = bundleDetailsData?.data?.lockRedeemableOnline;
  const lockVisibleOnline = bundleDetailsData?.data?.lockVisibleOnline;
  const lockedSettingsDescription = renderLockedSettings(bundleDetailsData?.data);
  const isBundleAddOnsEnabled = useBundleAddOnsFeatureFlag();

  const filteredStoreGroups = useMemo(
    () => storeGroupsDataWithoutStore?.filter((group) => group.chosenPrice || group.status),
    [storeGroupsDataWithoutStore],
  );

  const [missingItemsModal, setMissingItemsModal] = useState<{
    groupId: string;
    storeGroupName: string;
  } | null>(null);

  const handleMissingItemsClick = useCallback((group: StoreGroup) => {
    setMissingItemsModal({
      groupId: group.groupId,
      storeGroupName: group.groupName,
    });
  }, []);

  const renderRowLeadingCell = useCallback(
    ({ row }: { row: Row<StoreGroup> }) =>
      row.original.isVariantMissing ? (
        <div className={sharedTableStyles.rowLeadingCellContent}>
          <Icon
            iconName="AlertCircle"
            size={24}
            className={styles.missingVariantsAlertIcon}
            data-testid="missing-variants-alert-icon"
            onClick={(event) => {
              event.stopPropagation();
              handleMissingItemsClick(row.original);
            }}
          />
        </div>
      ) : null,
    [handleMissingItemsClick],
  );

  let selectedGroups: StoreGroup[];
  if (!hideInactive) {
    selectedGroups = storeGroupsDataWithoutStore
      ? Object.keys(selectedRows)?.map((rowIndex) => storeGroupsDataWithoutStore[+rowIndex])
      : [];
  } else {
    selectedGroups = filteredStoreGroups
      ? Object.keys(selectedRows)?.map((rowIndex) => filteredStoreGroups[+rowIndex])
      : [];
  }

  const selectedIds = selectedGroups.map((group) => group.groupId);
  const chosenPrices = selectedGroups.map((group) => group.chosenPrice);
  const availablePrices = bundleDetailsData?.data?.availablePrices || [];
  const filteredChosenPrices = chosenPrices.filter((price) => availablePrices.includes(price));
  const recommendedPrices = bundleDetailsData?.data?.recommendedPrices || [];
  const priceOptionsData = [...recommendedPrices, ...filteredChosenPrices]
    .sort((a, b) => a - b)
    .filter((price) => price !== null);
  const priceOptionsDataUnique = [...new Set(priceOptionsData)];
  const priceOptions = priceOptionsDataUnique.map((price: number) => ({
    value: price.toFixed(2) || '',
    label: `$${price.toFixed(2)}` || '',
  }));
  const [modalDefaultSettings, setModalDefaultSettings] = useState<
    { pricePoint: string; redeemableOnline: boolean; visibleOnline: boolean } | undefined
  >(undefined);

  useEffect(() => {
    const findModalDefaultSettings = () => {
      if (selectedGroups.length === 0) {
        return undefined;
      }
      let prevSelectedGroup = selectedGroups[0];
      for (const selectedGroup of selectedGroups) {
        if (
          selectedGroup.chosenPrice !== prevSelectedGroup.chosenPrice ||
          selectedGroup.chosenRedeemableOnline !== prevSelectedGroup.chosenRedeemableOnline ||
          selectedGroup.chosenVisibleOnline !== prevSelectedGroup.chosenVisibleOnline
        ) {
          return undefined;
        }
        prevSelectedGroup = selectedGroup;
      }
      if (selectedGroups[0].chosenPrice === null) {
        return {
          pricePoint: 'null',
          redeemableOnline: selectedGroups[0].chosenRedeemableOnline,
          visibleOnline: selectedGroups[0].chosenVisibleOnline,
        };
      }
      return {
        pricePoint: `${selectedGroups[0].chosenPrice.toFixed(2)}`,
        redeemableOnline: selectedGroups[0].chosenRedeemableOnline,
        visibleOnline: selectedGroups[0].chosenVisibleOnline,
      };
    };
    setModalDefaultSettings(findModalDefaultSettings());
  }, [selectedRows]);

  const [redeemableOnline, setRedeemableOnline] = useState<boolean | undefined>(
    bundleDetailsData?.data?.lockRedeemableOnline,
  );
  const [visibleOnline, setVisibleOnline] = useState<boolean | undefined>(bundleDetailsData?.data?.lockVisibleOnline);

  useEffect(() => {
    setRedeemableOnline(bundleDetailsData?.data?.lockRedeemableOnline);
    setVisibleOnline(bundleDetailsData?.data?.isLockVisibleOnline);
  }, [bundleDetailsData?.data]);

  const handleEditClick = () => {
    setDropdownPriceOptions(priceOptions);
    setIsEditBundleSettingsModalOpen(true);
    if (isLockRedeemableOnline && isLockVisibleOnline) {
      setSelectedPrice(modalDefaultSettings?.pricePoint);
      setRedeemableOnline(lockRedeemableOnline);
      setVisibleOnline(lockVisibleOnline);
    } else if (!modalDefaultSettings || modalDefaultSettings.pricePoint === 'null') {
      setSelectedPrice(undefined);
    } else {
      setSelectedPrice(modalDefaultSettings.pricePoint);
      switch (modalDefaultSettings.redeemableOnline) {
        case true:
          setRedeemableOnline(true);
          if (modalDefaultSettings.visibleOnline) {
            setVisibleOnline(true);
          } else {
            setVisibleOnline(false);
          }
          break;
        case false:
          setRedeemableOnline(false);
          setVisibleOnline(false);
          break;
        default:
          break;
      }
    }
  };

  const handleTextLinkClick = () => {
    setHideInactive(!hideInactive);
  };

  const handleLinkedBundleClick = () => {
    setIsLinkedBundleModalOpen(true);
  };

  const handleEditClose = () => {
    setIsEditBundleSettingsModalOpen(false);
    setSelectedPrice(undefined);
    setSelectedRows({});
    setRedeemableOnline(undefined);
    setVisibleOnline(undefined);
    setDropdownPriceOptions([...priceOptions]);
  };

  const handleLinkedBundlesClose = () => {
    setIsLinkedBundleModalOpen(false);
  };

  const handleEditSuccess = () => {
    handleEditClose();
    setShowConfirmedModal(true);
    refetch();
    refetchBundleDetails();
    setDropdownPriceOptions([...priceOptions]);
  };

  const handleEditError = (error: unknown) => {
    const { message } = error as { message: string };
    if (message !== 'API Gateway request timed out') {
      showToast({
        label: t('error.updatingPricePoints'),
        toastType: ToastTypes.Error,
        id: 'errorUpdatingBundlePrices',
      });
    }
    handleEditClose();
    setDropdownPriceOptions([...priceOptions]);
  };

  const handleEditConfirm = () => {
    if (
      bundleDetailsData?.data?.isLockPrice &&
      bundleDetailsData?.data?.linkedBundleName &&
      selectedPrice !== 'deactivate' &&
      (selectedLinkedBundleGroups || []).length > 0
    ) {
      linkedBundleMutate(
        {
          groups: selectedLinkedBundleGroups || undefined,
          redeemableOnline,
          visibleOnline,
        },
        {
          onSuccess: handleEditSuccess,
          onError: handleEditError,
        },
      );
    } else if (selectedPrice === 'deactivate') {
      deactivateBundle(
        { allGroups: false, groups: selectedIds },
        {
          onSuccess: handleEditSuccess,
          onError: handleEditError,
        },
      );
    } else {
      mutate(
        {
          groups: selectedIds,
          price: Number(selectedPrice).toFixed(2),
          redeemableOnline,
          visibleOnline,
        },
        {
          onSuccess: handleEditSuccess,
          onError: handleEditError,
        },
      );
    }
  };

  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);

  const openDeactivateModal = () => {
    setDeactivateModalVisible(true);
  };

  const closeDeactivateModal = () => {
    setDeactivateModalVisible(false);
  };

  const handleDeactivateClick = () => {
    deactivateBundle(
      { allGroups: true, groups: [] },
      {
        onSuccess: () => {
          if (isInvisible)
            navigateWithToast('/bundles/active', {
              label: t('editBundle.bundleDetails.deactivatedBundle'),
              id: 'deactivateSuccess',
            });
          closeDeactivateModal();
          refetch();
          refetchBundleDetails();
        },
        onError: () => {
          showToast({
            label: t('error.deactivatingBundle'),
            toastType: ToastTypes.Error,
            id: 'errorFetchingStoreGroups',
          });
          closeDeactivateModal();
          resetDeactivate();
        },
      },
    );
  };

  const nationalProgramExists = [NationalProgramStates.LIVE, NationalProgramStates.OPT_IN].includes(
    nationalProgramData?.data.state as NationalProgramStates,
  );

  const npIsLive =
    nationalProgramData?.data.state === NationalProgramStates.LIVE && bundleDetailsData?.data?.isLocked === true;

  const previousVisibilityFalse = bundleDetailsData?.data?.previousVisibility === false;

  const hideSettingsDuringOptIn =
    (bundleDetailsData?.data?.previousVisibility === false || bundleDetailsData?.data?.previousVisibility === null) &&
    nationalProgramData?.data?.state === NationalProgramStates.OPT_IN;

  const storeGroupTagMessage = t('editBundle.bundleDetails.activeStoreGroupMessage', {
    count: activeStoreGroupCount ?? 1,
  });

  const { data: freezeBundleData } = useFreezeBundle(bundleId !== undefined, bundleId!);
  const freezeBundle = freezeBundleData?.data;

  const editButtonIsVisible =
    !npIsLive && !isInvisible && !isMaintenanceFreeze && !isViewOnly && !freezeExists && !freezeBundle;
  const deactivateButtonIsVisible = !isMaintenanceFreeze && !previousVisibilityFalse && !isViewOnly && !freezeExists;
  const showStoreGroupSelectionColumn = editButtonIsVisible || npIsLive;
  const hasAnyMissingVariants = storeGroupsDataWithoutStore?.some((group) => group.isVariantMissing) ?? false;

  const storeGroupAssignmentColumns = useMemo(
    () => [
      {
        id: 'group',
        accessorKey: 'groupName',
        header: t('editBundle.bundleSettings.table.storeGroup'),
        cell: StoreGroupCell,
      },
      ...(hasAnyMissingVariants
        ? [
            {
              id: 'missingItems',
              header: '',
              cell: MissingItemsCell,
            },
          ]
        : []),
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: t('editBundle.bundleSettings.table.updatedAt'),
        cell: (props: CellContext<StoreGroup, unknown>) =>
          props.row.original.updatedAt ? new Date(props.row.original.updatedAt).toLocaleDateString() : '-',
      },
      {
        id: 'chosenPrice',
        accessorKey: 'chosenPrice',
        header: t('editBundle.bundleSettings.table.pricePoint'),
        cell: ChosenPriceCell,
      },
      {
        id: 'chosenRedeemOnline',
        accessorKey: 'chosenRedeemOnline',
        header: t('editBundle.bundleSettings.table.redeemOnline'),
        cell: ChosenRedeemOnlineCell,
      },
      {
        id: 'chosenVisibleOnline',
        accessorKey: 'chosenVisibleOnline',
        header: t('editBundle.bundleSettings.table.visibleOnline'),
        cell: ChosenVisibleOnlineCell,
      },
      {
        id: 'status',
        accessorKey: 'status',
        cell: Status,
      },
    ],
    [hasAnyMissingVariants, t],
  );

  useEffect(() => {
    setIsLinkedBundleModalOpen(false);
  }, [bundleId]);

  return (
    <>
      <div className={styles.headerContainer}>
        <div>
          <Header
            title={bundleCode !== '-' && displayName !== '-' ? `${bundleCode} - ${displayName}` : ''}
            // additionalTitleInfo={{ label: 'Back to All Bundles' }}
            breadcrumb={{ label: t('breadcrumb.backToAllBundles'), to: '/bundles/library' }}
          />
        </div>
        <div className={styles.chip}>
          {isLinkedBundle && (
            <Chip
              avatar={
                <Avatar style={{ width: '20px', height: '20px', backgroundColor: 'white' }}>
                  <LinkIcon style={{ fontSize: '20px', color: '#266DCC' }} />
                </Avatar>
              }
              data-testid="icon"
              key="storeCount"
              label="Linked Bundles"
              color="default"
              variant="outlined"
              onClick={handleLinkedBundleClick}
              sx={{
                fontSize: '14px',
                fontWeight: '500',
                height: '22px',
                backgroundColor: 'white',
                color: '#266DCC',
                borderColor: '#266DCC',
                marginLeft: '10px',
                marginTop: '-5px',
              }}
            />
          )}
        </div>
      </div>
      <div
        className={classNames(styles.page, {
          [styles.withAlias]: alias !== null,
          [styles.withFreeze]: freezeExists,
        })}
      >
        <div className={classNames(styles.topContainer, styles.whiteBackground)}>
          <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
              <h2 className={styles.title}>{t('editBundle.bundleDetails.title')}</h2>
              {bundleDetailsData?.data?.fzGroupCount && activeStoreGroupCount !== 0 ? (
                <Tag
                  type={TagTypes.Completed}
                  label={npIsLive ? t('editBundle.bundleDetails.activeNationalProgram') : storeGroupTagMessage}
                />
              ) : (
                <div className={styles.pill}>
                  <Tag type={TagTypes.Inactive} label={t('common.inactive')} />
                  <Tooltip position={PositionTypes.Top} content={t('editBundle.bundleDetails.activateTooltip')} />
                </div>
              )}
            </div>
            <div>
              {deactivateButtonIsVisible && (
                <Button
                  data-testid="deactivate-bundle"
                  variant={ButtonVariants.Secondary}
                  onClick={openDeactivateModal}
                  disabled={
                    !bundleDetailsData?.data?.fzGroupCount || activeStoreGroupCount === 0 || npIsLive || freezeBundle
                  }
                >
                  {t('common.deactivate')}
                </Button>
              )}
              {/* out of scope for this story */}
              {/* <Button>Print</Button> */}
            </div>
          </div>
          <Card>
            <div className={styles.row}>
              <div>
                <img src={BundleDetailsPlaceholder} alt={t('editBundle.bundleDetails.bundleImagePlaceholderAlt')} />
              </div>
              <div>
                <div className={styles.displayDescriptions}>
                  <strong className={styles.displayLabel}>{t('editBundle.prices.displayNameLabel')}: </strong>
                  {bundleDetailsData?.data && <span>{displayName}</span>}
                </div>
                <div className={styles.displayDescriptions}>
                  <strong className={styles.displayLabel}>{t('editBundle.prices.bundleCodeLabel')}: </strong>
                  {bundleDetailsData?.data && <span>{bundleCode}</span>}
                </div>
                <div className={styles.displayDescriptions}>
                  <strong className={styles.displayLabel}>{t('editBundle.prices.bundleDescLabel')}: </strong>
                  {bundleDetailsData?.data && <span>{bundleDesc}</span>}
                </div>
                <div className={styles.displayDescriptions}>
                  <strong className={styles.displayLabel}>{t('editBundle.prices.pricesLabel')}: </strong>
                  <div className={styles.pricesAndUpchargeMessage}>
                    {bundleDetailsData?.data && <span>{prices}</span>}
                    {isUpchargeRequired && (
                      <div>
                        <Tag type={TagTypes.UpchargeRequired} label={t('bundleUpcharges.requiresUpcharge')} />
                        <Link to="/bundles/upcharges">{t('bundleUpcharges.manageUpcharge')}</Link>
                      </div>
                    )}
                  </div>
                </div>
                {hasAnyAddOnChoice && isBundleAddOnsEnabled && (
                  <div className={styles.displayDescriptions}>
                    <strong className={styles.displayLabel}>
                      {t('editBundle.bundleDetails.bundleAddOnPricesLabel')}{' '}
                    </strong>
                    <div>
                      <Link to="/bundles/add-ons" className={styles.manageAddOnPricesLink}>
                        {t('editBundle.bundleDetails.manageAddOnPricesLink')}
                      </Link>
                    </div>
                  </div>
                )}
                <div className={styles.displayDescriptions}>
                  <strong className={styles.displayLabel}>{t('editBundle.prices.occasionLabel')}: </strong>
                  {bundleDetailsData?.data && <span>{occasion}</span>}
                </div>
                <div className={styles.displayDescriptions}>
                  {codes && <strong className={styles.displayLabel}>{t('editBundle.prices.linkedToLabel')}: </strong>}
                  <div>
                    {codes &&
                      codes.map((obj, index) => (
                        <>
                          {index > 0 && ', '}
                          <Link
                            to={`/bundles/${obj.id}`}
                            className={index === 0 ? styles.firstLink : styles.normalLink}
                          >
                            {obj.code}
                          </Link>
                        </>
                      ))}
                  </div>
                </div>
                <div className={styles.displayDescriptions}>
                  {lockedSettingsDescription && (
                    <strong className={styles.displayLabel}>{t('editBundle.prices.lockedSettingsLabel')}: </strong>
                  )}
                  {lockedSettingsDescription && <span>{lockedSettingsDescription}</span>}
                </div>
              </div>
            </div>
            <h3>{t('editBundle.bundleDetails.bundleProductChoices.header')}</h3>
            <div className={styles.tableContainer}>
              <Table
                className={styles.bundleProductChoicesTableScroll}
                isLoading={bundleDetailsLoading}
                loadingRowCount={5}
                includeMinWidth={false}
                width="100%"
                data={productChoicesData || []}
                columns={[
                  {
                    accessorKey: 'choiceName',
                    header: t('editBundle.bundleDetails.bundleProductChoices.table.choiceName'),
                    enableSorting: true,
                  },
                  {
                    accessorKey: 'defaultProduct',
                    header: t('editBundle.bundleDetails.bundleProductChoices.table.product'),
                    enableSorting: true,
                  },
                  {
                    accessorKey: 'defaultVariant',
                    header: t('editBundle.bundleDetails.bundleProductChoices.table.variant'),
                    enableSorting: true,
                  },
                ]}
              />
            </div>
            {hasAnyAddOnChoice && (
              <p className={styles.bundleAddOnFootnote}>
                {t('editBundle.bundleDetails.bundleProductChoices.bundleAddOnFootnote')}
              </p>
            )}
          </Card>
        </div>
        {nationalProgramExists && nationalProgramGroups.length && (
          <NationalProgram
            {...nationalProgramData!.data}
            bundleGroups={nationalProgramGroups || []}
            refetch={refetchNationalProgram}
            isViewOnly={isViewOnly}
            redeemableOnline={nationalProgramData?.data?.nationalProgram?.redeemableOnline}
            visibleOnline={nationalProgramData?.data?.nationalProgram?.visibleOnline}
          />
        )}
        <div
          className={classNames(styles.bottomContainer, {
            [styles.greyBackground]:
              nationalProgramData?.data.state === NationalProgramStates.NA ||
              nationalProgramData?.data.state === undefined,
            [styles.whiteBackground]:
              nationalProgramData?.data.state !== NationalProgramStates.NA &&
              nationalProgramData?.data.state !== undefined,
            [styles.hidden]: hideSettingsDuringOptIn,
          })}
        >
          {nationalProgramExists && (
            <Card className={styles.tooltipCard}>
              <Icon iconName="Info" />
              <div>
                {npIsLive
                  ? t('editBundle.bundleSettings.pricePointsOverridden', {
                      date: DateTime.fromISO(nationalProgramData!.data.nationalProgram!.freezeEndDate, {
                        setZone: true,
                      }).toFormat('D'),
                    })
                  : t('editBundle.bundleSettings.pricePointsWillBeOverridden', {
                      date: DateTime.fromISO(nationalProgramData!.data.nationalProgram!.freezeStartDate, {
                        setZone: true,
                      }).toFormat('D'),
                    })}
              </div>
            </Card>
          )}
          {isInvisible && (
            <Card className={styles.tooltipCard}>
              <Icon iconName="Info" />
              <div>{t('editBundle.bundleSettings.bundleRemovedFromGlobalLibrary')}</div>
            </Card>
          )}
          {isMaintenanceFreeze && (
            <Card className={styles.tooltipCard}>
              <Icon iconName="Info" />
              <div>
                {t('editBundle.bundleSettings.bundleUnderMaintenance', {
                  date: DateTime.fromISO(maintenanceFreezeData!.data.freezeEndDate).toFormat('D'),
                })}
              </div>
            </Card>
          )}
          {hasAnyMissingVariants && (
            <Card className={styles.missingItemsAlertCard}>
              <Icon iconName="AlertCircle" size={24} className={styles.missingItemsAlertIcon} />
              <div>{t('editBundle.bundleSettings.missingItemsReminder')}</div>
            </Card>
          )}
          <Card>
            <div className={styles.tableHeader}>
              <h3>{t('editBundle.bundleSettings.bundleGroupAssignments')}</h3>
              {!npIsLive && (
                <Button variant={ButtonVariants.Text} onClick={handleTextLinkClick}>
                  {hideInactive ? t('common.showInactive') : t('common.hideInactive')}
                </Button>
              )}
            </div>
            <div className={styles.storeGroupAssignmentsTableWrapper}>
              <Table
                className={classNames(styles.storeGroupAssignmentsTable, {
                  [styles.storeGroupAssignmentsTableHasMissingItems]: hasAnyMissingVariants,
                })}
                selectedRows={selectedRows}
                enableRowSelection={
                  showStoreGroupSelectionColumn ? (row) => editButtonIsVisible && !row.original.isVariantMissing : false
                }
                // The missing-variants icon should still be visible when the bundle is locked;
                // it only affects row actions (selection/edit), not the informational modal.
                renderRowLeadingCell={hasAnyMissingVariants ? renderRowLeadingCell : undefined}
                isLoading={storeGroupsLoading}
                loadingRowCount={7}
                includeMinWidth={false}
                data={hideInactive ? filteredStoreGroups || [] : storeGroupsDataWithoutStore || []}
                headersToWrap={['chosenVisibleOnline', 'chosenRedeemOnline']}
                columns={storeGroupAssignmentColumns}
                onRowSelectionChange={setSelectedRows}
              />
            </div>
            {!nationalProgramExists && (
              <CalendarAlert
                className={styles.calendarAlert}
                requestedVerbiage={false}
                addCalendarLink={true}
                warningIcon={true}
              ></CalendarAlert>
            )}
            {editButtonIsVisible && (
              <div className={styles.editButton}>
                <Button onClick={handleEditClick} disabled={Object.keys(selectedRows).length < 1}>
                  {t('editBundle.bundleSettings.editBundleSettingsButton')}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
      {isEditBundleSettingsModalOpen && (
        <EditBundleSettingsModal
          isOpen={isEditBundleSettingsModalOpen}
          onClose={handleEditClose}
          onConfirm={handleEditConfirm}
          isLockPrice={bundleDetailsData?.data?.isLockPrice}
          isLoading={editBundlePriceLoading || deactivateIsLoading || editLinkedBundleLoading}
          availablePrices={bundleDetailsData?.data?.availablePrices}
          isLinkedBundle={bundleDetailsData?.data?.linkedBundleName !== null}
          recommendedPrices={recommendedPrices}
          priceOptions={!priceOptions ? [] : priceOptions}
          dropdownPriceOptions={
            !dropdownPriceOptions
              ? []
              : [
                  ...dropdownPriceOptions,
                  {
                    label: t('common.deactivate'),
                    value: 'deactivate',
                  },
                ]
          }
          setDropdownPriceOptions={setDropdownPriceOptions}
          selectedRows={selectedGroups}
          selectedPrice={selectedPrice}
          setSelectedPrice={setSelectedPrice}
          modalDefaultSettings={modalDefaultSettings}
          redeemableOnline={redeemableOnline}
          visibleOnline={visibleOnline}
          setRedeemableOnline={setRedeemableOnline}
          setVisibleOnline={setVisibleOnline}
          isLockRedeemableOnline={bundleDetailsData?.data?.isLockRedeemableOnline}
          isLockVisibleOnline={bundleDetailsData?.data?.isLockVisibleOnline}
          lockRedeemableOnline={bundleDetailsData?.data?.lockRedeemableOnline}
          lockVisibleOnline={bundleDetailsData?.data?.lockVisibleOnline}
          bundleId={bundleId}
          setSelectedLinkedBundleGroups={setSelectedLinkedBundleGroups}
          liveNp={nationalProgramData?.data.state === NationalProgramStates.LIVE}
        />
      )}
      <LinkedBundleModal
        isOpen={isLinkedBundleModalOpen}
        onClose={() => handleLinkedBundlesClose()}
        onConfirm={handleEditConfirm}
        isLoading={editBundlePriceLoading || deactivateIsLoading}
        selectedRows={bundleDetailsData?.data.bundleLinkedToDto || []}
        linkedBundleName={bundleDetailsData?.data.linkedBundleName || []}
      />
      <DeactivateBundleModal
        isOpen={deactivateModalVisible}
        onClose={closeDeactivateModal}
        groups={storeGroupsDataWithoutStore || []}
        onConfirm={handleDeactivateClick}
        isLoading={deactivateIsLoading}
      />
      {missingItemsModal && bundleId && (
        <MissingProductsModal
          isOpen={!!missingItemsModal}
          onClose={() => setMissingItemsModal(null)}
          bundleId={bundleId}
          groupId={missingItemsModal.groupId}
          storeGroupName={missingItemsModal.storeGroupName}
        />
      )}
      <ConfirmationModal
        isOpen={showConfirmedModal}
        onClose={() => setShowConfirmedModal(false)}
        title={t('common.requestSent')}
      >
        {isLinkedBundle && <LinkedBundle />}
      </ConfirmationModal>
    </>
  );
};

export default BundleDetails;
